// AgentSubAgentDetail — Phase 16.6 agent-side detail page for a
// DIRECT downline sub-agent.
//
// Five tabs:
//   1. Identity   — read-only: username, email, status, parent, level
//   2. Players    — list of players directly owned by this sub-agent
//   3. Wallet     — balance + transfer-from-my-pool form (pure transfer)
//   4. Rates      — Phase 15.3 partnership matrix (golden-limit clamped)
//   5. Operations — suspend/unsuspend toggle + password reset
//
// Backend gate: every endpoint here is /admin/me-agent/sub-agents/:id
// (or /admin/me-agent/partnership/children/:childId). All five backend
// handlers route through loadDirectChildOr404, which returns 404 for
// any agent that isn't a DIRECT child of the caller. Stranger agents
// or grandchildren get a clean 404 with no info leak.

import { useEffect, useState, type ReactNode } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import AdminLayout from '@baaligames/shared-ui/admin/components/AdminLayout';
import AdminAvatar from '@baaligames/shared-ui/admin/components/AdminAvatar';
// H7: the sub-agent Rates tab now hosts the per-agent PartnershipEditor —
// a parent (Super Master / Master) sets this child's win/loss %. The old
// The old Pioneer-derived RatesTab block was removed (2026-07-04).
import PartnershipEditor from '@baaligames/shared-ui/admin/components/PartnershipEditor';
import SportRatesEditor from '@baaligames/shared-ui/admin/components/SportRatesEditor';
import Icon from '@baaligames/shared-ui/components/Icon';
import PhoneContact from '@baaligames/shared-ui/components/PhoneContact';
// Phase 16.8: switched the inline tab JSX to the shared SegmentedTabs
// primitive so player + agent + sub-agent detail pages all share one
// source of truth for the tab visual.
import { SegmentedTabs, type TabDef } from '@baaligames/shared-ui/admin/components/TabHub';
import { adminApi } from '@baaligames/shared-ui/admin/lib/adminApi';
import { useAdminAuth } from '@baaligames/shared-ui/admin/lib/adminAuth';
import { ApiError } from '@baaligames/shared-ui/lib/api';
import { useT } from '@baaligames/shared-ui/lib/i18n';
import LocationCapture, {
  type GeoBag,
} from '@baaligames/shared-ui/components/LocationCapture';

type SubAgent = {
  id: number;
  // Phase R5 — sub-agent's Unique Agent ID. Parent agent sees this
  // (read-only, copyable) so they can hand it out for transfers
  // INTO this child without exposing the child's internal db PK.
  uaid: string | null;
  // 2026-05-18 — Identity masking. The structured display_name is
  // composed server-side (first_name + last_name). The raw username
  // is still on the sub-agent record (parents can technically see it
  // in some response paths) but peer rendering uses display_name
  // only — the existing JSX has been updated to render display_name
  // with a dash placeholder fallback.
  display_name: string | null;
  first_name: string | null;
  last_name:  string | null;
  birthdate:  string | null;
  username: string;
  // Phase C4 — Identity Stack on direct sub-agent profile. Backend
  // returns phone via AgentModel.get → AGENT_PUBLIC_COLS.
  phone: string | null;
  email: string;
  avatar_url: string | null;
  status: 'active' | 'suspended';
  parent_admin_id: number | null;
  parent_agent_id?: number | null;
  permissions: Record<string, 'none' | 'view' | 'edit'>;
  wallet_balance: number;
  owned_players: number;
  created_at: string;
  last_login_at: string | null;
  identity_locked: 0 | 1 | boolean;
  // Phase R4 — sub-agent locator opt-out (parent override).
  is_accepting_players: 0 | 1 | boolean;
  // R3/R4 — structured geo. Edited via PATCH
  // /admin/me-agent/sub-agents/:id/location (R4 Bug 2 endpoint).
  // Phase R6 (2026-05-13): migration 069 added pincode_id to agents.
  // The backend JOIN now returns `pincode` (6-digit string) alongside
  // the FK so the LocationCapture summary card can render it without
  // a second round-trip.
  latitude:       string | number | null;
  longitude:      string | number | null;
  state_id:       number | null;
  district_id:    number | null;
  area_id:        number | null;
  pincode_id:     number | null;
  pincode:        string | null;
  nation:         string | null;
  geo_updated_at: string | null;
};

type PlayerRow = {
  id: number; username: string; first_name?: string | null; last_name?: string | null; email: string; phone: string | null;
  status: string; created_at: string; wallet_balance: number;
};

type RateMatrix = {
  child: { id: number; username: string; level: number; status: string; child_count: number };
  rates: Array<{ pillar: string; game_id: string; rate_pct: number; updated_at: string }>;
  ceilings: Array<{ pillar: string; game_id: string; ceiling_pct: number }>;
  // Phase 5b (2026-05-15): same data shape as myRates uses for the
  // Direct/Indirect 3-column grid. See meAgentPartnership.getChildMatrix.
  nextLevelRates?: Array<{ pillar: string; game_id: string; rate_pct: number }>;
  levelWeights?: Record<string, number>;
  adminWeight?: number;
};

type Tab = 'identity' | 'players' | 'wallet' | 'rates' | 'ops';

// Tab labels resolved at render via t() so language changes reflow
// reactively. `iconOnly` static metadata stays here; copy moves to
// the translation dict under agent.subAgentDetail.tab.*.
const TABS_META: Array<{ key: Tab; labelKey: string; icon: string }> = [
  { key: 'identity', labelKey: 'agent.subAgentDetail.tab.identity', icon: 'badge' },
  { key: 'players',  labelKey: 'agent.subAgentDetail.tab.players',  icon: 'group' },
  { key: 'wallet',   labelKey: 'agent.subAgentDetail.tab.wallet',   icon: 'account_balance_wallet' },
  { key: 'rates',    labelKey: 'agent.subAgentDetail.tab.rates',    icon: 'percent' },
  { key: 'ops',      labelKey: 'agent.subAgentDetail.tab.ops',      icon: 'manage_accounts' },
];

const inr = (n: number) => `₹${(n || 0).toLocaleString('en-IN')}`;

export default function AgentSubAgentDetail() {
  const t = useT();
  const { id } = useParams<{ id: string }>();
  const subId = Number(id);
  const nav = useNavigate();
  const { admin: me } = useAdminAuth();

  const [tab, setTab] = useState<Tab>('identity');
  const [sa, setSa] = useState<SubAgent | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  async function load() {
    setErr(null);
    try {
      const r = await adminApi.get<{ agent: SubAgent }>(`/admin/me-agent/sub-agents/${subId}`);
      setSa(r.agent);
    } catch (e) {
      setErr((e as ApiError).message || t('agent.subAgentDetail.error.loadFailed'));
    }
  }
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [subId]);

  // Resolve tab labels via t() at render time.
  const TABS: TabDef[] = TABS_META.map((m) => ({ key: m.key, label: t(m.labelKey), icon: m.icon }));

  if (err) {
    return (
      <AdminLayout title={t('agent.subAgentDetail.title')} titleIcon="support_agent">
        <div className="rounded-2xl bg-rose-500/10 border border-rose-400/30
                        text-rose-700 dark:text-rose-200 px-4 py-4">
          <div className="font-bold mb-1">{err}</div>
          <button onClick={() => nav('/people/agents/list')} className="text-xs underline">
            {t('agent.subAgentDetail.backToList')}
          </button>
        </div>
      </AdminLayout>
    );
  }

  if (!sa) {
    return (
      <AdminLayout title={t('agent.subAgentDetail.title')} titleIcon="support_agent">
        <div className="text-sm text-arena-600 dark:text-neon-200/70">{t('agent.subAgentDetail.loading')}</div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title={(sa.display_name || '').trim() || '—'} titleIcon="support_agent" subtitle={t('agent.subAgentDetail.subtitle')}>
      {/* Back to the sub-agent roster (2026-08-06 UX fix — the success
          view had no back affordance, and the old target redirected to
          the recharge queue instead of the list). */}
      <div className="mb-2">
        <Link to="/people/agents/list"
              className="inline-flex items-center gap-1 text-[12px] font-semibold
                         text-arena-700 hover:text-arena-900
                         dark:text-neon-200/80 dark:hover:text-white transition">
          <Icon name="arrow_back" size={14} /> {t('agent.subAgentDetail.backToList')}
        </Link>
      </div>
      {toast && (
        <div className="mb-3 px-4 py-2 rounded-xl bg-emerald-500/10 border border-emerald-400/30
                        text-emerald-700 dark:text-emerald-200 text-sm">
          {toast}
        </div>
      )}

      {/* Header card */}
      <div className="mb-3 rounded-2xl p-4 bg-white dark:bg-arena-700/40
                      border border-arena-200/60 dark:border-arena-600/50
                      flex items-center gap-3">
        {/* 2026-05-18 — Identity masking. Avatar uses display_name as
            initials seed; the username + email row is removed from
            this peer-facing view. The header title shows display_name
            with a dash placeholder when profile is incomplete. */}
        <AdminAvatar url={sa.avatar_url} username={(sa.display_name || '').trim() || sa.username || '—'} size="lg" ring />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <h2 className="text-lg font-bold truncate text-arena-900 dark:text-white">
              {/* 2026-05-25 — fall back to username when display name is unset
                  so an incomplete-profile sub-agent never shows a bare "—". */}
              <span className={(sa.display_name || '').trim() ? '' : 'opacity-60'}>
                {(sa.display_name || '').trim() || sa.username || '—'}
              </span>
            </h2>
            <StatusPill status={sa.status} />
          </div>
          {/* Phase C4 — Identity Stack on direct sub-agent header.
              Display name is the H2 above; line 2 = @username, line 3
              = phone. Both are visible because the parent agent is a
              DIRECT upline. */}
          <div className="text-[12px] text-arena-600 dark:text-neon-200/70 truncate font-mono">
            @{sa.username} <span className="text-arena-400 dark:text-neon-200/60">·</span> #{sa.id}
          </div>
          {sa.phone && (
            <PhoneContact phone={sa.phone} name={sa.username ?? sa.display_name} iconSize={11}
              className="text-[12px] text-arena-700 dark:text-neon-200/80 truncate tabular-nums inline-flex items-center gap-1 hover:text-arena-900 dark:hover:text-neon-200" />
          )}
          <div className="mt-1 flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px] text-arena-600 dark:text-neon-200/70">
            <span>{t(sa.owned_players === 1 ? 'agent.subAgentDetail.header.players.one' : 'agent.subAgentDetail.header.players.other', { count: sa.owned_players })}</span>
            <span className="text-emerald-700 dark:text-emerald-300 tabular-nums font-bold">
              {t('agent.subAgentDetail.header.pool', { amount: inr(sa.wallet_balance) })}
            </span>
            {sa.last_login_at && (
              <span>{t('agent.subAgentDetail.header.lastLogin', { when: new Date(sa.last_login_at).toLocaleString() })}</span>
            )}
          </div>
        </div>
      </div>

      {/* Phase 16.8: tab strip via shared SegmentedTabs primitive. */}
      <div className="mb-3">
        <SegmentedTabs
          variant="block"
          tabs={TABS}
          activeKey={tab}
          onTabChange={(k) => setTab(k as Tab)}
        />
      </div>

      {/* Body */}
      {tab === 'identity'  && <IdentityTab sa={sa} me={me} onSaved={(t) => { setToast(t); load(); setTimeout(() => setToast(null), 3500); }} />}
      {tab === 'players'   && <PlayersTab subId={subId} />}
      {tab === 'wallet'    && <WalletTab sa={sa} subId={subId} onDone={(t) => { setToast(t); load(); setTimeout(() => setToast(null), 3500); }} />}
      {tab === 'rates'     && <div className="max-w-lg space-y-5">
        <PartnershipEditor agentId={subId} onSaved={() => { setToast('Partnership rate updated'); setTimeout(() => setToast(null), 3500); }} />
        <div className="border-t border-arena-200/60 dark:border-white/10 pt-4"><SportRatesEditor agentId={subId} onSaved={() => { setToast('Sport rate updated'); setTimeout(() => setToast(null), 3500); }} /></div>
      </div>}
      {tab === 'ops'       && <OpsTab sa={sa} subId={subId} onDone={(t) => { setToast(t); load(); setTimeout(() => setToast(null), 3500); }} />}
    </AdminLayout>
  );
}

// ─── Identity tab ─────────────────────────────────────────────────

function IdentityTab({ sa, me, onSaved }: { sa: SubAgent; me: any; onSaved: (t: string) => void }) {
  const t = useT();
  return (
    <div className="space-y-4">
      <div className="rounded-2xl bg-white dark:bg-arena-700/40
                      border border-arena-200/60 dark:border-arena-600/50 divide-y divide-neutral-200 dark:divide-white/10">
        {/* Phase R5 — sub-agent's Unique Agent ID. Parent sees this
            so they can dictate it back to the sub-agent (who can
            then share it for incoming transfers) or paste it into
            their own PlayerTransferModal to send a player TO this
            sub-agent without going through the chain hierarchy. */}
        {sa.uaid && (
          <Row k={t('agent.subAgentDetail.identity.row.uaid')} v={
            <span className="font-mono tabular-nums tracking-[.16em] text-[13px] text-cyan-700 dark:text-cyan-300 select-all">
              {sa.uaid}
            </span>
          } />
        )}
        {/* 2026-05-18 — Identity masking. The legacy Username row is
            REPLACED by a Display Name row. The username remains the
            sub-agent's login credential but is not surfaced in any
            peer view — including this one (the parent agent looking
            at their downline child is a peer view by definition). */}
        <Row k={t('agent.subAgentDetail.identity.row.username')} v={
          <span className={(sa.display_name || '').trim() ? '' : 'opacity-60'}>
            {(sa.display_name || '').trim() || '—'}
          </span>
        } />
        <Row k={t('agent.subAgentDetail.identity.row.status')}   v={<StatusPill status={sa.status} />} />
        <Row k={t('agent.subAgentDetail.identity.row.parent')} v={
          /* Parent identity (the calling agent themselves). They are
             reading their OWN identity here so display_name is the
             friendly form. The translation key still expects a
             {username} interpolation slot for back-compat. */
          me
            ? t('agent.subAgentDetail.identity.parentValue', {
                username: ((me as any).display_name || `${(me as any).first_name || ''} ${(me as any).last_name || ''}`).trim() || '—',
              })
            : t('agent.subAgentDetail.identity.parentEmpty')
        } />
        <Row k={t('agent.subAgentDetail.identity.row.ownedPlayers')} v={String(sa.owned_players)} />
        <Row k={t('agent.subAgentDetail.identity.row.wallet')} v={
          <span className={`tabular-nums font-bold ${sa.wallet_balance >= 0 ? 'text-emerald-700 dark:text-emerald-300' : 'text-rose-600 dark:text-rose-400'}`}>
            {inr(sa.wallet_balance)}
          </span>
        } />
        <Row k={t('agent.subAgentDetail.identity.row.created')} v={new Date(sa.created_at).toLocaleString()} />
        {sa.last_login_at && <Row k={t('agent.subAgentDetail.identity.row.lastLogin')} v={new Date(sa.last_login_at).toLocaleString()} />}
        <Row k={t('agent.subAgentDetail.identity.row.identityLocked')} v={sa.identity_locked
          ? t('agent.subAgentDetail.identity.identityLocked.yes')
          : t('agent.subAgentDetail.identity.identityLocked.no')} />
        {/* Phase R4 — parent agent toggles sub-agent's locator opt-out.
            Hits PATCH /admin/me-agent/sub-agents/:id/opt-out which
            audits as `agent.opt_out_change_by_parent`. */}
        <Row k={t('agent.subAgentDetail.identity.row.receiving')}>
          <SubAgentOptOutToggle sa={sa} onSaved={onSaved} />
        </Row>
      </div>

      {/* R4 Bug 2 — parent agent corrects sub-agent's location.
          Hits PATCH /admin/me-agent/sub-agents/:id/location. The
          backend audit log records this as
          `agent.location_update_by_parent` so admin can distinguish
          parent-edits from self-edits in the forensic timeline.
          GPS hidden (creator-mode): parent's own GPS would mis-
          attribute every cold signup in the sub-agent's territory. */}
      <SubAgentLocationEditor sa={sa} onSaved={onSaved} />
    </div>
  );
}

// ─── Phase R4 — parent toggles sub-agent locator opt-out ───────────
function SubAgentOptOutToggle({ sa, onSaved }: { sa: SubAgent; onSaved: (m: string) => void }) {
  const t = useT();
  const [accepting, setAccepting] = useState<boolean>(!!sa.is_accepting_players);
  const [busy, setBusy] = useState(false);

  useEffect(() => { setAccepting(!!sa.is_accepting_players); }, [sa.is_accepting_players]);

  async function flip() {
    if (busy) return;
    const newVal = !accepting;
    setAccepting(newVal);          // optimistic
    setBusy(true);
    try {
      await adminApi.patch(`/admin/me-agent/sub-agents/${sa.id}/opt-out`, { accepting: newVal });
      onSaved(newVal
        ? t('agent.subAgentDetail.optOut.toast.accepting')
        : t('agent.subAgentDetail.optOut.toast.optedOut'));
    } catch (e) {
      setAccepting(!newVal);       // rollback
      onSaved((e as ApiError).message || t('agent.subAgentDetail.optOut.error.updateFailed'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="flex items-center gap-2">
      <span className={`inline-block px-2 py-0.5 rounded-full text-[11px] border ${
        accepting
          ? 'bg-emerald-50 dark:bg-emerald-500/20 text-emerald-700 dark:text-emerald-300 border-emerald-400 dark:border-emerald-500/50'
          : 'bg-amber-100 dark:bg-amber-500/20 text-amber-800 dark:text-amber-200 border-amber-400 dark:border-amber-500/50'
      }`}>
        <Icon name={accepting ? 'task_alt' : 'pause_circle'} size={11} className="inline -mt-0.5 mr-0.5" />
        {accepting
          ? t('agent.subAgentDetail.optOut.accepting')
          : t('agent.subAgentDetail.optOut.optedOut')}
      </span>
      <button
        type="button"
        onClick={flip}
        disabled={busy}
        aria-pressed={accepting}
        className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full
                    transition disabled:opacity-50 ${accepting ? 'bg-emerald-600' : 'bg-amber-500'}`}>
        <span className={`inline-block h-4 w-4 rounded-full bg-white shadow transition-transform ${
          accepting ? 'translate-x-6' : 'translate-x-1'
        }`} />
      </button>
    </div>
  );
}

// ─── R4 Bug 2 — parent-edits-sub-agent location ────────────────────
function SubAgentLocationEditor({
  sa, onSaved,
}: { sa: SubAgent; onSaved: (msg: string) => void }) {
  const t = useT();
  // Phase R6 (2026-05-13): forward pincode_id + pincode string from the
  // backend record so the LocationCapture saved-summary card displays
  // "Pin code: NNNNNN" instantly without a second API hop.
  const initial: GeoBag = {
    latitude:    sa.latitude    != null ? Number(sa.latitude)  : null,
    longitude:   sa.longitude   != null ? Number(sa.longitude) : null,
    state_id:    sa.state_id    ?? null,
    district_id: sa.district_id ?? null,
    area_id:     sa.area_id     ?? null,
    pincode_id:  sa.pincode_id  ?? null,
    pincode:     sa.pincode     ?? null,
    nation:      sa.nation || 'India',
  };
  const [geo, setGeo] = useState<GeoBag>(initial);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    setGeo({
      latitude:    sa.latitude    != null ? Number(sa.latitude)  : null,
      longitude:   sa.longitude   != null ? Number(sa.longitude) : null,
      state_id:    sa.state_id    ?? null,
      district_id: sa.district_id ?? null,
      area_id:     sa.area_id     ?? null,
      pincode_id:  sa.pincode_id  ?? null,
      pincode:     sa.pincode     ?? null,
      nation:      sa.nation || 'India',
    });
  }, [sa.latitude, sa.longitude, sa.state_id, sa.district_id, sa.area_id, sa.pincode_id, sa.pincode, sa.nation]);

  const dirty =
    (initial.latitude    ?? null) !== (geo.latitude    ?? null) ||
    (initial.longitude   ?? null) !== (geo.longitude   ?? null) ||
    (initial.state_id    ?? null) !== (geo.state_id    ?? null) ||
    (initial.district_id ?? null) !== (geo.district_id ?? null) ||
    (initial.area_id     ?? null) !== (geo.area_id     ?? null) ||
    (initial.pincode_id  ?? null) !== (geo.pincode_id  ?? null) ||
    (initial.nation      ?? 'India') !== (geo.nation   ?? 'India');

  async function save() {
    setBusy(true); setErr(null);
    try {
      const patch: Record<string, unknown> = {};
      const keys: (keyof GeoBag)[] = ['latitude','longitude','state_id','district_id','area_id','pincode_id','nation'];
      for (const k of keys) {
        const before = (initial[k] ?? null) as unknown;
        const now    = (geo[k]     ?? null) as unknown;
        if (before !== now) patch[k] = geo[k];
      }
      if (Object.keys(patch).length === 0) {
        onSaved(t('agent.subAgentDetail.location.nothingToSave'));
        return;
      }
      await adminApi.patch(`/admin/me-agent/sub-agents/${sa.id}/location`, patch);
      const n = Object.keys(patch).length;
      onSaved(t(n === 1 ? 'agent.subAgentDetail.location.toast.saved.one' : 'agent.subAgentDetail.location.toast.saved.other', { count: n }));
    } catch (e) {
      setErr((e as ApiError).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="rounded-2xl bg-white dark:bg-arena-700/40
                    border border-arena-200/60 dark:border-arena-600/50 p-4">
      <div className="flex items-center justify-between mb-3">
        <div className="text-[12px] font-bold uppercase tracking-wider text-arena-700 dark:text-neon-200/70 inline-flex items-center gap-1.5">
          <Icon name="my_location" size={14} className="text-fuchsia-600 dark:text-fuchsia-400" />
          {t('agent.subAgentDetail.location.title')} {sa.geo_updated_at && (
            <span className="font-normal lowercase opacity-60 normal-case">
              {t('agent.subAgentDetail.location.lastUpdated', { date: new Date(sa.geo_updated_at).toLocaleDateString() })}
            </span>
          )}
        </div>
        <button
          onClick={save}
          disabled={!dirty || busy}
          className="px-3 py-1.5 rounded-lg text-[12px] font-bold uppercase tracking-wider
                     bg-emerald-600 hover:bg-emerald-500 active:bg-emerald-700 text-white
                     inline-flex items-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed">
          {busy ? <Icon name="progress_activity" size={12} className="animate-spin" /> : <Icon name="save" size={12} />}
          {t('agent.subAgentDetail.location.cta.save')}
        </button>
      </div>
      <LocationCapture
        key={sa.geo_updated_at ?? 'new'}
        value={geo}
        onChange={setGeo}
        mode="child"                       // creator-mode: parent's GPS would clobber sub-agent; pincode only
        disabled={busy}
        inputClassName={inputCls}
      />
      {err && (
        <p className="text-[11.5px] text-rose-600 dark:text-rose-300 mt-2">{err}</p>
      )}
      {!dirty && !err && (
        <p className="text-[11px] text-arena-600 dark:text-neon-200/65 mt-2">
          {t('agent.subAgentDetail.location.hint')}
        </p>
      )}
    </div>
  );
}

// ─── Players tab ──────────────────────────────────────────────────

function PlayersTab({ subId }: { subId: number }) {
  const t = useT();
  const [data, setData] = useState<{ rows: PlayerRow[]; total: number } | null>(null);
  const [err, setErr] = useState<string | null>(null);
  useEffect(() => {
    (async () => {
      try {
        const r = await adminApi.get<{ rows: PlayerRow[]; total: number }>(
          `/admin/me-agent/sub-agents/${subId}/players?limit=100`
        );
        setData(r);
      } catch (e) { setErr((e as ApiError).message); }
    })();
  }, [subId]);

  if (err) return <ErrorBox text={err} />;
  if (!data) return <div className="text-sm text-arena-600 dark:text-neon-200/70">{t('agent.subAgentDetail.loading')}</div>;
  if (data.rows.length === 0) {
    return (
      <div className="rounded-2xl bg-white dark:bg-arena-700/40
                      border border-arena-200/60 dark:border-arena-600/50 p-6 text-center text-sm text-arena-600 dark:text-neon-200/70">
        {t('agent.subAgentDetail.players.empty')}
      </div>
    );
  }
  return (
    <div className="rounded-2xl bg-white dark:bg-arena-700/40
                    border border-arena-200/60 dark:border-arena-600/50 overflow-hidden">
      <div className="px-4 py-2.5 text-[11px] uppercase tracking-wider font-bold
                      text-arena-600 dark:text-neon-200/60 border-b border-arena-200/60 dark:border-arena-600/50">
        {t(data.total === 1 ? 'agent.subAgentDetail.players.count.one' : 'agent.subAgentDetail.players.count.other', { count: data.total })}
      </div>
      <table className="w-full text-sm">
        <tbody>
          {data.rows.map((p) => (
            <tr key={p.id} className="border-t border-arena-200/60 dark:border-arena-600/40 first:border-t-0">
              <td className="px-4 py-2.5">
                <div className="font-bold text-arena-900 dark:text-white">{(((p.first_name || "") + " " + (p.last_name || "")).trim() || "—")}</div>
                <div className="text-[11px] text-arena-600 dark:text-neon-200/70">{p.phone || p.email || '—'}</div>
              </td>
              <td className="px-4 py-2.5 text-right tabular-nums font-bold text-emerald-700 dark:text-emerald-300">
                {inr(p.wallet_balance)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ─── Wallet tab ───────────────────────────────────────────────────

// Phase 16.7 — Wallet tab with bidirectional transfer + full ledger.
//
// Dual forms side-by-side: TOPUP (parent → child, green) and
// WITHDRAW (child → parent, amber). Same atomic-transfer backend
// pattern; the sub-agent sees the mirror leg in their own wallet
// ledger automatically.
//
// Ledger panel below shows the FULL sub-agent wallet ledger — every
// row regardless of kind. The parent can see not just our parent↔
// child transfers but also the sub-agent's payouts to players,
// recharge commissions, revenue share, etc. Each row's source_ref
// disambiguates parent transfers (parent:N) from other movements.

type LedgerRow = {
  id: number;
  agent_id: number;
  kind: string;
  amount: number;
  source_user_id: number | null;
  source_ref: string | null;
  note: string | null;
  balance_after: number | null;
  actor_admin_id: number | null;
  actor_agent_id: number | null;
  created_at: string;
};

// Kind label resolution lives in the translation dict. Each ledger kind
// has a labelKey looked up via t() at render time so the same row says
// "Recharge commission" in English and the equivalent in Hindi etc.
const KIND_META: Record<string, { labelKey: string; icon: string }> = {
  topup_from_admin:    { labelKey: 'agent.subAgentDetail.wallet.kind.topupFromAdmin',     icon: 'add_circle' },
  topup_to_player:     { labelKey: 'agent.subAgentDetail.wallet.kind.topupToPlayer',      icon: 'send' },
  topup_from_player:   { labelKey: 'agent.subAgentDetail.wallet.kind.topupFromPlayer',    icon: 'payments' },
  revenue_share:       { labelKey: 'agent.subAgentDetail.wallet.kind.revenueShare',       icon: 'pie_chart' },
  recharge_commission: { labelKey: 'agent.subAgentDetail.wallet.kind.rechargeCommission', icon: 'savings' },
  co_fund_offer:       { labelKey: 'agent.subAgentDetail.wallet.kind.coFundOffer',        icon: 'redeem' },
  admin_adjust:        { labelKey: 'agent.subAgentDetail.wallet.kind.adminAdjust',        icon: 'tune' },
  withdrawal_hold:     { labelKey: 'agent.subAgentDetail.wallet.kind.withdrawalHold',     icon: 'lock_clock' },
  withdrawal_refund:   { labelKey: 'agent.subAgentDetail.wallet.kind.withdrawalRefund',   icon: 'undo' },
  platform_fee_share:  { labelKey: 'agent.subAgentDetail.wallet.kind.platformFeeShare',   icon: 'account_balance' },
  win_loss_share:      { labelKey: 'agent.subAgentDetail.wallet.kind.winLossShare',       icon: 'casino' },
};

function WalletTab({ sa, subId, onDone }: { sa: SubAgent; subId: number; onDone: (t: string) => void }) {
  const t = useT();
  const [ledger, setLedger] = useState<{ rows: LedgerRow[]; total: number; balance: number } | null>(null);
  const [ledgerErr, setLedgerErr] = useState<string | null>(null);

  async function loadLedger() {
    try {
      const r = await adminApi.get<{ rows: LedgerRow[]; total: number; balance: number }>(
        `/admin/me-agent/sub-agents/${subId}/wallet/ledger?limit=100`
      );
      setLedger(r);
    } catch (e) { setLedgerErr((e as ApiError).message); }
  }
  useEffect(() => { loadLedger(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [subId]);

  // Phase 16.x refactor — the inline parent↔sub-agent TransferForm that
  // used to live here has been removed. It duplicated the central
  // ManualSubAgentRechargeForm rendered on the Agents requests panel.
  // We now surface a single "Manual Transaction" CTA that deep-links to
  // that central form with this sub-agent pre-selected, so there is one
  // money-movement entry point across the app. The clawback / "anti-
  // snatch" flow is the same as before — sub-agent submits a withdrawal
  // request, parent approves it from the same requests panel.

  return (
    <div className="space-y-3">
      {/* Balance card */}
      <div className="rounded-2xl bg-white dark:bg-arena-700/40
                      border border-arena-200/60 dark:border-arena-600/50 p-4">
        <div className="text-[11px] uppercase tracking-wider font-bold text-arena-600 dark:text-neon-200/60 mb-1">
          {t('agent.subAgentDetail.wallet.balanceLabel')}
        </div>
        <div className={`font-display font-black text-3xl tabular-nums
                         ${sa.wallet_balance >= 0 ? 'text-emerald-700 dark:text-emerald-300' : 'text-rose-600 dark:text-rose-400'}`}>
          {inr(sa.wallet_balance)}
        </div>
      </div>

      {/* Manual Transaction CTA — single entry point. Routes to the
          central agent-requests panel (Recharge sub-tab) with this
          sub-agent pre-selected via ?preselect=. */}
      <div className="rounded-2xl bg-white dark:bg-arena-700/40
                      border border-emerald-200 dark:border-emerald-400/30 p-4">
        <Link
          to={`/people/agents/requests?preselect=${subId}`}
          className="w-full inline-flex items-center justify-center gap-2 rounded-xl
                     px-4 py-3 text-[13px] font-bold transition active:scale-95
                     bg-emerald-600 hover:bg-emerald-500 active:bg-emerald-700 text-white
                     shadow-[0_2px_10px_rgba(16,185,129,0.35)]"
        >
          <Icon name="arrow_outward" size={16} />
          {t('agent.subAgentDetail.wallet.cta.manualTransaction')}
        </Link>
        <p className="text-[11.5px] text-arena-600 dark:text-neon-200/75 mt-2 text-center">
          {t('agent.subAgentDetail.wallet.cta.manualTransactionHint')}
        </p>
      </div>

      {/* Full sub-agent ledger — every wallet movement, every kind.
          Parent transfers (kind=admin_adjust + source_ref=parent:N)
          are highlighted with a "you ↔ them" badge so they're easy
          to spot among the rest of the activity. */}
      <div className="rounded-2xl bg-white dark:bg-arena-700/40
                      border border-arena-200/60 dark:border-arena-600/50 overflow-hidden">
        <div className="px-4 py-2.5 border-b border-arena-200/60 dark:border-arena-600/50
                        bg-arena-50/40 dark:bg-arena-700/25
                        flex items-baseline justify-between">
          <h4 className="text-[11px] uppercase tracking-wider font-bold text-arena-600 dark:text-neon-200/60">
            {t('agent.subAgentDetail.wallet.ledger.title', { username: (sa.display_name || '').trim() || '—' })}
          </h4>
          <span className="text-[10px] text-arena-500 dark:text-neon-200/55">
            {t('agent.subAgentDetail.wallet.ledger.subtitle')}
          </span>
        </div>
        {ledgerErr && <div className="px-4 py-3"><ErrorBox text={ledgerErr} /></div>}
        {!ledgerErr && ledger === null && (
          <div className="px-4 py-6 text-sm text-arena-600 dark:text-neon-200/70">{t('agent.subAgentDetail.loading')}</div>
        )}
        {ledger && ledger.rows.length === 0 && (
          <div className="px-4 py-6 text-sm text-arena-600 dark:text-neon-200/70 text-center">
            {t('agent.subAgentDetail.wallet.ledger.empty')}
          </div>
        )}
        {ledger && ledger.rows.length > 0 && (
          /* Phase 19 hot-fix: rebuilt to match the player-detail
             wallet ledger so every wallet ledger across the platform
             looks the same. Stacked card rows with colored icon disc,
             signed amount + running balance on the right. The
             agent-side "you ↔ them" cyan badge is preserved — it's
             a domain-specific cue (parent-sub-agent transfers vs.
             generic admin adjustments) that only appears here. */
          <ul className="space-y-1.5 max-h-[420px] overflow-y-auto px-3 pb-3">
            {ledger.rows.map((r) => {
              const meta = KIND_META[r.kind];
              const label = meta ? t(meta.labelKey) : r.kind;
              const icon = meta?.icon || 'circle';
              const isParentTransfer = r.source_ref === `parent:${(sa as any).parent_agent_id ?? ''}` || (r.actor_agent_id != null && r.source_ref?.startsWith('parent:'));
              const positive = r.amount > 0;
              const negative = r.amount < 0;
              return (
                <li key={r.id}
                    className="flex items-center gap-2.5 rounded-xl px-3 py-2.5
                               bg-arena-50/40 dark:bg-arena-700/30
                               border border-arena-200/60 dark:border-arena-600/50">
                  <span className={`grid place-items-center w-7 h-7 rounded-full shrink-0 ${
                    positive ? 'bg-emerald-50 text-emerald-700 dark:bg-emerald-400/15 dark:text-emerald-300'
                    : negative ? 'bg-rose-50 text-rose-700 dark:bg-rose-400/15 dark:text-rose-300'
                    : 'bg-arena-100/60 text-arena-700 dark:bg-arena-600/40 dark:text-neon-200/80'
                  }`}>
                    <Icon name={icon} size={14} />
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <span className="text-[12.5px] font-semibold text-arena-900 dark:text-white">
                        {label}
                      </span>
                      {isParentTransfer && (
                        <span className="inline-flex items-center gap-1 text-[9.5px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded
                                         bg-cyan-50 text-cyan-700 dark:bg-cyan-400/15 dark:text-cyan-300">
                          {t('agent.subAgentDetail.wallet.ledger.youThemBadge')}
                        </span>
                      )}
                    </div>
                    {r.note && (
                      <div className="text-[11px] text-arena-700 dark:text-neon-200/75 truncate">
                        {r.note}
                      </div>
                    )}
                    <div className="text-[10px] text-arena-600 dark:text-neon-200/65">
                      {new Date(r.created_at).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' })}
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className={`text-[13px] font-bold tabular-nums ${
                      positive ? 'text-emerald-700 dark:text-emerald-300'
                      : negative ? 'text-rose-700 dark:text-rose-300'
                      : 'text-arena-700 dark:text-neon-200/80'
                    }`}>
                      {positive ? '+' : ''}₹{Math.abs(r.amount).toLocaleString('en-IN')}
                    </div>
                    {r.balance_after != null && (
                      <div className="text-[10px] text-arena-600 dark:text-neon-200/65 tabular-nums">
                        {t('agent.subAgentDetail.wallet.ledger.balanceAfter', { amount: r.balance_after.toLocaleString('en-IN') })}
                      </div>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}

// ─── Rates tab (removed 2026-07-04) ──────────────────
// The sub-agent Rates tab is now the shared PartnershipEditor
// (per-agent win/loss %, golden limit), rendered inline in the tab
// switch above. The old Pioneer-derived RatesTab + PIONEER_PCTS +
// pioneer* / fmt* / ValueColumn helpers were all removed here.

// ─── Operations tab ───────────────────────────────────────────────

function OpsTab({ sa, subId, onDone }: { sa: SubAgent; subId: number; onDone: (t: string) => void }) {
  const t = useT();
  const [reason, setReason] = useState('');
  const [newPwd, setNewPwd] = useState('');
  const [busy, setBusy] = useState<null | 'status' | 'pwd'>(null);
  const [err, setErr] = useState<string | null>(null);

  const flipStatus = async () => {
    const target = sa.status === 'active' ? 'suspended' : 'active';
    // Parallel confirmation messages per direction — each translator-
    // managed so the verb form stays natural per language (suspend vs
    // unsuspend often need different connectives in Indic languages).
    const confirmMsg = target === 'suspended'
      ? t('agent.subAgentDetail.ops.status.confirm.suspend',   { username: (sa.display_name || '').trim() || '—' })
      : t('agent.subAgentDetail.ops.status.confirm.unsuspend', { username: (sa.display_name || '').trim() || '—' });
    if (!confirm(confirmMsg)) return;
    setBusy('status'); setErr(null);
    try {
      await adminApi.post(`/admin/me-agent/sub-agents/${subId}/status`,
        target === 'suspended' ? { status: 'suspended', reason: reason || null } : { status: 'active' });
      onDone(t(target === 'suspended'
        ? 'agent.subAgentDetail.ops.status.toast.suspended'
        : 'agent.subAgentDetail.ops.status.toast.active', { username: (sa.display_name || '').trim() || '—' }));
      setReason('');
    } catch (e) { setErr((e as ApiError).message); }
    finally { setBusy(null); }
  };

  const resetPwd = async () => {
    if (newPwd.length < 8) { setErr(t('agent.subAgentDetail.ops.password.error.tooShort')); return; }
    if (!confirm(t('agent.subAgentDetail.ops.password.confirm', { username: (sa.display_name || '').trim() || '—' }))) return;
    setBusy('pwd'); setErr(null);
    try {
      await adminApi.post(`/admin/me-agent/sub-agents/${subId}/password-reset`, { new_password: newPwd });
      onDone(t('agent.subAgentDetail.ops.password.toast.reset', { username: (sa.display_name || '').trim() || '—' }));
      setNewPwd('');
    } catch (e) { setErr((e as ApiError).message); }
    finally { setBusy(null); }
  };

  return (
    <div className="space-y-3">
      {err && <ErrorBox text={err} />}

      {/* Suspend / unsuspend */}
      <div className="rounded-2xl bg-white dark:bg-arena-700/40
                      border border-arena-200/60 dark:border-arena-600/50 p-4 space-y-2">
        <div className="flex items-baseline justify-between">
          <h4 className="text-sm font-bold text-arena-900 dark:text-white">{t('agent.subAgentDetail.ops.status.title')}</h4>
          <StatusPill status={sa.status} />
        </div>
        <p className="text-[11px] text-arena-600 leading-relaxed dark:text-neon-200/70">
          {t('agent.subAgentDetail.ops.status.body')}
        </p>
        {sa.status === 'active' && (
          <input type="text" maxLength={190} className={inputCls}
                 placeholder={t('agent.subAgentDetail.ops.status.reasonPlaceholder')}
                 value={reason} onChange={(e) => setReason(e.target.value)} />
        )}
        <div className="flex justify-end">
          <button type="button" onClick={flipStatus} disabled={busy === 'status'}
                  className={`px-4 py-2 rounded-lg text-xs font-bold uppercase tracking-wider text-white disabled:opacity-50
                              ${sa.status === 'active' ? 'bg-amber-600 hover:bg-amber-500' : 'bg-emerald-600 hover:bg-emerald-500'}`}>
            {busy === 'status'
              ? t('agent.subAgentDetail.ops.status.cta.working')
              : sa.status === 'active'
                ? t('agent.subAgentDetail.ops.status.cta.suspend')
                : t('agent.subAgentDetail.ops.status.cta.unsuspend')}
          </button>
        </div>
      </div>

      {/* Password reset */}
      <div className="rounded-2xl bg-white dark:bg-arena-700/40
                      border border-arena-200/60 dark:border-arena-600/50 p-4 space-y-2">
        <h4 className="text-sm font-bold text-arena-900 dark:text-white">{t('agent.subAgentDetail.ops.password.title')}</h4>
        <p className="text-[11px] text-arena-600 leading-relaxed dark:text-neon-200/70">
          {t('agent.subAgentDetail.ops.password.body')}
        </p>
        <input type="password" maxLength={128} className={inputCls}
               placeholder={t('agent.subAgentDetail.ops.password.placeholder')}
               value={newPwd} onChange={(e) => setNewPwd(e.target.value)} />
        <div className="flex justify-end">
          <button type="button" onClick={resetPwd} disabled={busy === 'pwd' || newPwd.length < 8}
                  className="px-4 py-2 rounded-lg text-xs font-bold uppercase tracking-wider
                             bg-violet-600 text-white hover:bg-violet-500 disabled:opacity-50">
            {busy === 'pwd'
              ? t('agent.subAgentDetail.ops.password.cta.resetting')
              : t('agent.subAgentDetail.ops.password.cta.reset')}
          </button>
        </div>
      </div>

      {/* Permissions — parent edits the sub-agent's capability grants */}
      <PermissionsSection sa={sa} subId={subId} onDone={onDone} />
    </div>
  );
}

// ─── Permissions editor (parent → direct sub-agent) ───────────────
// Agent-side mirror of the admin permission matrix. Lists every
// capability and lets the parent set none / view / edit for the child.
// The PARENT'S OWN level is the ceiling: a button above what the parent
// holds is disabled (the server clamps too — this is honest UI). Gated
// on the parent having create_subagent=edit.

const PERMISSION_LABELS: Record<string, { label: string; hint: string }> = {
  users:             { label: 'Players (view/edit)',  hint: 'See and edit owned player profiles' },
  users_create:      { label: 'Create players',       hint: 'Onboard new player accounts' },
  users_status:      { label: 'Suspend/ban players',  hint: "Change a player's status" },
  wallet_adjust:     { label: 'Adjust player wallet', hint: 'Direct credit/debit a player' },
  wallet_topup:      { label: 'Recharge a player',    hint: 'Send money from own pool to a player' },
  payments_recharge: { label: 'Recharge requests',    hint: 'Approve/reject recharge submissions' },
  payments_withdraw: { label: 'Withdrawal requests',  hint: 'Mark paid / reject withdrawals' },
  payment_settings:  { label: 'UPI / QR config',      hint: 'Edit own payment receiver settings' },
  rewards_offers:    { label: 'Reward claim feed',    hint: 'See offer credits to owned players' },
  rewards_create:    { label: 'Create offers',        hint: 'Create offers funded from own pool' },
  earnings:          { label: 'Earnings ledger',      hint: 'See own commission/revenue history' },
  bulk_payouts:      { label: 'Bulk payouts',         hint: 'Run bulk credits to many players' },
  create_subagent:   { label: 'Create sub-agents',    hint: 'Onboard agents under self' },
  partnership_downline: { label: 'Downline rates',    hint: "See sub-agents' rate matrix" },
};

const PERM_RANK: Record<string, number> = { none: 0, view: 1, edit: 2 };

type PermSchema = {
  keys: string[];
  levels: Record<string, string[]>;
  defaults: Record<string, 'none'|'view'|'edit'>;
  myPermissions: Record<string, 'none'|'view'|'edit'>;
};

function PermissionsSection({ sa, subId, onDone }: { sa: SubAgent; subId: number; onDone: (t: string) => void }) {
  const [schema, setSchema]   = useState<PermSchema | null>(null);
  const [perm, setPerm]       = useState<Record<string, 'none'|'view'|'edit'>>(sa.permissions || {});
  const [initial, setInitial] = useState<Record<string, 'none'|'view'|'edit'>>(sa.permissions || {});
  const [busy, setBusy]       = useState(false);
  const [err, setErr]         = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    adminApi.get<PermSchema>('/admin/me-agent/permissions/schema')
      .then((s) => { if (alive) setSchema(s); })
      .catch((e) => { if (alive) setErr((e as ApiError).message); });
    return () => { alive = false; };
  }, []);

  useEffect(() => {
    setPerm(sa.permissions || {});
    setInitial(sa.permissions || {});
  }, [sa.id]); // eslint-disable-line react-hooks/exhaustive-deps

  if (err && !schema) {
    return (
      <div className="rounded-2xl bg-white dark:bg-arena-700/40 border border-arena-200/60 dark:border-arena-600/50 p-4">
        <h4 className="text-sm font-bold text-arena-900 dark:text-white mb-1">Permissions</h4>
        <ErrorBox text={err} />
      </div>
    );
  }
  if (!schema) {
    return (
      <div className="rounded-2xl bg-white dark:bg-arena-700/40 border border-arena-200/60 dark:border-arena-600/50 p-4 text-[12px] text-arena-500">
        Loading permissions…
      </div>
    );
  }

  const myPerms = schema.myPermissions || {};
  // Only a parent who can manage sub-agents may retune their powers.
  const canEdit = (myPerms.create_subagent || 'none') === 'edit';
  const isRowChanged = (k: string) => (perm[k] || 'none') !== (initial[k] || 'none');
  const dirty = schema.keys.some(isRowChanged);

  const save = async () => {
    setBusy(true); setErr(null);
    try {
      await adminApi.patch(`/admin/me-agent/sub-agents/${subId}/permissions`, { permissions: perm });
      setInitial(perm);
      onDone('Permissions updated');
    } catch (e) { setErr((e as ApiError).message); }
    finally { setBusy(false); }
  };

  return (
    <div className="rounded-2xl bg-white dark:bg-arena-700/40 border border-arena-200/60 dark:border-arena-600/50 p-4 space-y-2">
      <h4 className="text-sm font-bold text-arena-900 dark:text-white">Permissions</h4>
      <p className="text-[11px] text-arena-600 leading-relaxed dark:text-neon-200/70">
        Set what this sub-agent can do. <b>none</b> hides it, <b>view</b> is read-only, <b>edit</b> grants full access.
        You can only grant up to the level you hold yourself.
      </p>
      {err && <ErrorBox text={err} />}
      {!canEdit && (
        <div className="text-[11px] rounded-lg bg-amber-50 dark:bg-amber-500/10 border border-amber-300/60 dark:border-amber-400/30 text-amber-800 dark:text-amber-200 px-3 py-2">
          You need the "Create sub-agents" permission (edit) to change a sub-agent's permissions.
        </div>
      )}

      <div className="rounded-xl ring-1 ring-inset ring-arena-200/50 dark:ring-arena-600/40 overflow-hidden">
        {schema.keys.map((k) => {
          const levels = schema.levels[k] || ['none', 'edit'];
          const cur = perm[k] || 'none';
          const meta = PERMISSION_LABELS[k] || { label: k, hint: '' };
          const cap = myPerms[k] || 'none';                  // parent's ceiling
          const changed = isRowChanged(k);
          return (
            <div key={k} className={`border-t border-arena-200/60 dark:border-arena-600/40 first:border-t-0 px-3 py-2.5 flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3 ${
              changed ? 'bg-amber-50/70 dark:bg-amber-500/[0.06]' : ''
            }`}>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-bold text-arena-800 dark:text-neon-200/90">{meta.label}</div>
                <div className="text-[11px] text-arena-600 dark:text-neon-200/60">{meta.hint}</div>
              </div>
              <div className="flex gap-1 self-stretch sm:self-auto">
                {(['none','view','edit'] as const).map((lvl) => {
                  if (!levels.includes(lvl)) return null;
                  // Disable any level above what the parent holds, and all
                  // controls if the parent can't edit permissions at all.
                  const overCap = PERM_RANK[lvl] > (PERM_RANK[cap] ?? 0);
                  const disabled = !canEdit || overCap;
                  const active = cur === lvl;
                  return (
                    <button key={lvl} type="button" disabled={disabled}
                      onClick={() => setPerm({ ...perm, [k]: lvl })}
                      title={overCap ? `You only hold "${cap}" on this — can't grant higher` : undefined}
                      className={`flex-1 sm:flex-initial px-2.5 py-1 rounded-md text-[11px] font-bold uppercase tracking-wider border transition ${
                        active
                          ? (lvl === 'edit' ? 'bg-emerald-600 border-emerald-700 text-white'
                            : lvl === 'view' ? 'bg-cyan-600 border-cyan-700 text-white'
                            : 'bg-violet-600 border-violet-700 text-white')
                          : 'bg-white dark:bg-arena-800 border-arena-200/60 dark:border-arena-600/40 text-arena-400 dark:text-neon-200/70 hover:bg-arena-100/60 dark:hover:bg-arena-700/60'
                      } ${disabled ? 'opacity-40 cursor-not-allowed' : ''}`}>
                      {lvl}
                    </button>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>

      <div className="flex justify-end">
        <button type="button" onClick={save} disabled={!dirty || busy || !canEdit}
                className="px-4 py-2 rounded-lg text-xs font-bold uppercase tracking-wider
                           bg-emerald-600 text-white hover:bg-emerald-500 disabled:opacity-50">
          {busy ? 'Saving…' : 'Save permissions'}
        </button>
      </div>
    </div>
  );
}

// ─── Reusable bits ────────────────────────────────────────────────

const inputCls =
  'w-full rounded-lg border border-arena-300/60 dark:border-arena-600/50 bg-white dark:bg-arena-700/40 ' +
  'text-sm text-arena-900 dark:text-white px-3 py-2 ' +
  'focus:outline-none focus:ring-2 focus:ring-emerald-400/40';

function Row({ k, v, children }: { k: string; v?: React.ReactNode; children?: React.ReactNode }) {
  return (
    <div className="flex items-center px-4 py-2.5">
      <div className="w-32 sm:w-44 text-[11px] uppercase tracking-wider font-bold text-arena-600 dark:text-neon-200/60">{k}</div>
      <div className="flex-1 text-sm text-arena-900 dark:text-white">{v ?? children}</div>
    </div>
  );
}

function StatusPill({ status }: { status: 'active' | 'suspended' }) {
  const t = useT();
  return status === 'active' ? (
    <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded
                     bg-emerald-50 text-emerald-700 dark:bg-emerald-400/15 dark:text-emerald-300">{t('agent.subAgentDetail.status.active')}</span>
  ) : (
    <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded
                     bg-rose-50 text-rose-700 dark:bg-rose-400/15 dark:text-rose-300">{t('agent.subAgentDetail.status.suspended')}</span>
  );
}

function ErrorBox({ text }: { text: string }) {
  return (
    <div className="rounded-xl bg-rose-500/10 border border-rose-400/30
                    text-rose-700 dark:text-rose-200 text-xs px-3 py-2 whitespace-pre-line">
      {text}
    </div>
  );
}
